import React from 'react';
import PermissionDenied from 'modules/Common/layout/PermissionDenied';
import PermissionsErrorState from './PermissionsErrorState';

/**
 * The one place a route-level authorization decision becomes UI.
 *
 * An authorization decision has FOUR outcomes, not two:
 *
 *   pending  "I don't know yet"      -> the caller's loader (default: nothing)
 *   error    "I couldn't find out"   -> PermissionsErrorState, retryable
 *   denied   "No"                    -> PermissionDenied, RENDERED IN PLACE
 *   allowed  "Yes"                   -> children
 *
 * A denial NEVER navigates. `<Redirect>` is a history.replace in react-router
 * 5.3.4, and that is a one-way door: it unmounts the guard that made the
 * decision, so when the inputs resolve a beat later nothing re-evaluates and
 * the user is stranded on "404 - you do not have permission" while being fully
 * entitled. Six commits each closed one race window (e47a21977, aef870ef6,
 * 50405fc1f, e5053aec5, 49628792c, 8f5a82cb7) and the bug outlived all of them,
 * because that strategy needs every source of lateness enumerated in advance.
 *
 * Rendering the denial instead keeps this component mounted and subscribed to
 * its inputs. A late resolution re-renders it and the page appears — no
 * navigation, no remembered destination, and no need to have predicted the
 * race. It self-heals for the windows we know about (the 30s provisioning
 * grace expiry, OrgBoundary's 15s switch watchdog, a Clerk org whose
 * publicMetadata.type has not landed) and for the ones nobody has found yet.
 *
 * See docs/features/denial-never-navigates/00-OVERVIEW.md — N1, N2, N5, N6.
 *
 * `pending` is never a denial: an unresolved input is not a "no". Callers pass
 * whatever they rendered while loading before, so this changes no timing.
 *
 * @param {'loading'|'error'|'ready'} status  readiness of the decision INPUTS
 * @param {Boolean} allowed  the decision itself; only read when status is 'ready'
 * @param {String} [requiredPermission]  permission slug shown to the user for
 *   diagnosis. Omit where the requirement is not a permission slug (an
 *   entitlement, an org type) — a misleading code is worse than none.
 * @param {React.ReactNode} [pending]  rendered while status is 'loading'
 */
/**
 * Map a react-query result onto the gate's `status` vocabulary.
 *
 * Guards whose readiness comes from a plain query (rather than
 * PermissionsProvider) need this, and getting it wrong is how a FAILED request
 * gets reported to the user as "you do not have permission" — which is what
 * CustomsRepGuard did before it used this: `!declarant?.isCustomsRepresentative`
 * is true both when the answer is no AND when there is no answer at all.
 *
 * CAUTION: a DISABLED query (`enabled: false`) is `isPending: true` forever in
 * v5, so this reports 'loading' forever and the gate defers forever. That is
 * the correct reading — a query that never runs never authorises anything — but
 * it means you must not gate a route on a query you disable. No current caller
 * does: CustomsRepGuard's `useFetchDeclarant()` takes the default `enabled: true`.
 */
export const queryStatus = ({ isPending, isError }) => {
  // `isPending`, NOT `isLoading`. In TanStack v5 `isLoading = isPending &&
  // isFetching`, so a PAUSED query — offline, under the default
  // networkMode: 'online' — reports isLoading: false, isError: false and would
  // fall through to 'ready', turning a network condition into "Access Denied".
  if (isPending) return 'loading';
  if (isError) return 'error';
  return 'ready';
};

const RouteAccessGate = ({
  status,
  allowed,
  requiredPermission,
  pending = null,
  onRetry,
  children,
}) => {
  if (status === 'loading') return pending;
  if (status === 'error') return <PermissionsErrorState onRetry={onRetry} />;

  // An unrecognised status is not permission to decide. Falling through to the
  // denial below would rule on inputs of unknown readiness — precisely what N5
  // forbids — and react-query's own vocabulary ('pending' / 'success') is the
  // obvious way to arrive here by accident. Defer instead, and complain loudly
  // in development. Deliberately not a throw: a stuck loader is recoverable,
  // a blank crashed page in production is not.
  if (status !== 'ready') {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(
        `[RouteAccessGate] Unknown status "${status}". Expected 'loading' | 'error' | 'ready'. ` +
          'Deferring rather than deciding. Map a query result with queryStatus().',
      );
    }
    return pending;
  }

  if (!allowed) return <PermissionDenied requiredPermission={requiredPermission} />;
  return children;
};

export default RouteAccessGate;
